import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, User, RefreshCw, Trophy, Keyboard, MousePointer2 } from "lucide-react";
import GameOptions from "./GameOptions"; // Reusing even if simpler options needed
import PlayerNameInput from "./PlayerNameInput";

// --------------------------------------------------------------------------
// Constants & Pure Helpers
// --------------------------------------------------------------------------
const SIZE = 4;
const SOLVED_STATE = Array.from({ length: 16 }, (_, i) => (i + 1) % 16); // 1, 2... 15, 0

const isSolvable = (board) => {
    let inversions = 0;
    for (let i = 0; i < board.length - 1; i++) {
        for (let j = i + 1; j < board.length; j++) {
            if (board[i] !== 0 && board[j] !== 0 && board[i] > board[j]) {
                inversions++;
            }
        }
    }
    
    const blankIndex = board.indexOf(0);
    const blankRowFromBottom = SIZE - Math.floor(blankIndex / SIZE); // 1-based count from bottom

    if (blankRowFromBottom % 2 === 0) {
        return inversions % 2 !== 0;
    } else {
        return inversions % 2 === 0;
    }
};

const isSolved = (board) => {
    for (let i = 0; i < board.length; i++) {
        if (board[i] !== SOLVED_STATE[i]) return false;
    }
    return true;
};

const getValidMoves = (board) => {
    const blankIdx = board.indexOf(0);
    const row = Math.floor(blankIdx / SIZE);
    const col = blankIdx % SIZE;
    const moves = [];

    // Up (blank moves up, means tile above moves down)
    if (row > 0) moves.push(blankIdx - SIZE);
    // Down
    if (row < SIZE - 1) moves.push(blankIdx + SIZE);
    // Left
    if (col > 0) moves.push(blankIdx - 1);
    // Right
    if (col < SIZE - 1) moves.push(blankIdx + 1);

    return { blankIdx, moves };
};

const makeMove = (board, tileIdx) => {
    const blankIdx = board.indexOf(0);
    
    // Check adjacency
    const bRow = Math.floor(blankIdx / SIZE);
    const bCol = blankIdx % SIZE;
    const tRow = Math.floor(tileIdx / SIZE);
    const tCol = tileIdx % SIZE;
    
    const dist = Math.abs(bRow - tRow) + Math.abs(bCol - tCol);
    if (dist !== 1) return board; // Invalid move

    const newBoard = [...board];
    [newBoard[blankIdx], newBoard[tileIdx]] = [newBoard[tileIdx], newBoard[blankIdx]];
    return newBoard;
};

const getHeuristic = (board) => {
    let dist = 0;
    for (let i = 0; i < board.length; i++) {
        const val = board[i];
        if (val === 0) continue;
        
        const targetIdx = val - 1; // 1 goes to 0, etc.
        const tr = Math.floor(targetIdx / SIZE);
        const tc = targetIdx % SIZE;
        
        const cr = Math.floor(i / SIZE);
        const cc = i % SIZE;
        
        dist += Math.abs(tr - cr) + Math.abs(tc - cc);
    }
    return dist;
};

// --------------------------------------------------------------------------
// Subcomponents (Defined outside to prevent unmounting on every render)
// --------------------------------------------------------------------------

const Tile = React.memo(({ val, onClick, isInteractable }) => {
    const empty = val === 0;
    return (
        <div 
          className={`
              flex items-center justify-center text-2xl font-bold rounded-lg
              transition-transform duration-100 select-none
              ${empty ? 'invisible' : 'bg-amber-100 text-amber-900 border-2 border-amber-200 shadow-sm'}
              ${isInteractable && !empty ? 'cursor-pointer hover:bg-amber-200 active:scale-95' : ''}
          `}
          style={{ width: '60px', height: '60px' }}
          onClick={empty ? undefined : onClick}
        >
            {val}
        </div>
    );
});

const BoardView = React.memo(({ board, setFn, label, color, isLocalPlayer, playerId, gameStarted, gameOver }) => {
    const handleTileClick = (idx) => {
        if (!isLocalPlayer || gameOver || !gameStarted) return;

        setFn(prev => {
           const res = makeMove(prev, idx);
           return res;
        });
    };

    return (
        <div className="flex flex-col items-center p-4 bg-white/50 rounded-xl">
           <div className={`text-xl font-bold mb-4 flex items-center gap-2 ${color}`}>
               {label}
           </div>
           <div className="grid grid-cols-4 gap-2 bg-amber-800 p-2 rounded-lg shadow-inner">
               {board.map((val, idx) => (
                   <Tile 
                      key={`${idx}-${val}`} 
                      val={val} 
                      onClick={() => handleTileClick(idx)}
                      isInteractable={isLocalPlayer && gameStarted && !gameOver}
                   />
               ))}
           </div>
           {isLocalPlayer && (
               <div className="mt-4 text-sm text-gray-500 flex items-center gap-2">
                   <Keyboard className="w-4 h-4" /> 
                   {playerId === 1 ? 'WASD' : 'Arrows'}
                   <span className="mx-1">|</span>
                   <MousePointer2 className="w-4 h-4" /> Click
               </div>
           )}
        </div>
    );
});

const FifteenPuzzleGame = ({ onBack }) => {
  const navigate = useNavigate();

  // Settings
  const [gameMode, setGameMode] = useState("PvC"); // PvC or PVP
  const [difficulty, setDifficulty] = useState("Easy");
  const [player1Name, setPlayer1Name] = useState("Player 1");
  const [player2Name, setPlayer2Name] = useState("Player 2");

  // Game State
  const [p1Board, setP1Board] = useState([]);
  const [p2Board, setP2Board] = useState([]);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);

  const shuffleBoard = () => {
      let newBoard;
      do {
          newBoard = [...SOLVED_STATE];
          for (let i = newBoard.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [newBoard[i], newBoard[j]] = [newBoard[j], newBoard[i]];
          }
      } while (!isSolvable(newBoard) || isSolved(newBoard));
      return newBoard;
  };

  const botIntervalRef = useRef(null);

  const startNextGame = () => {
    const initial = shuffleBoard();
    setP1Board([...initial]);
    setP2Board([...initial]);
    setGameStarted(true);
    setGameOver(false);
    setWinner(null);
  };

  // --------------------------------------------------------------------------
  // Check Win Condition
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    if (isSolved(p1Board)) {
        setGameOver(true);
        setWinner(1);
    } else if (isSolved(p2Board)) {
        setGameOver(true);
        setWinner(2);
    }
  }, [p1Board, p2Board, gameStarted, gameOver]);


  // --------------------------------------------------------------------------
  // Bot Logic
  // --------------------------------------------------------------------------
  useEffect(() => {
      if (!gameStarted || gameOver || gameMode !== "PvC") {
          if (botIntervalRef.current) {
              clearInterval(botIntervalRef.current);
              botIntervalRef.current = null;
          }
          return;
      }

      if (!botIntervalRef.current) {
          const speed = difficulty === "Easy" ? 800 : (difficulty === "Medium" ? 500 : 200);
          botIntervalRef.current = setInterval(() => {
              setP2Board(prev => {
                  if (isSolved(prev)) return prev;

                  const { blankIdx, moves } = getValidMoves(prev);
                  
                  let bestMove = -1;
                  let minH = Infinity;
                  const possibleMoves = [];

                  const shuffledMoves = moves.sort(() => 0.5 - Math.random());

                  for (let mIdx of shuffledMoves) {
                      const tempBoard = [...prev];
                      [tempBoard[blankIdx], tempBoard[mIdx]] = [tempBoard[mIdx], tempBoard[blankIdx]];
                      const h = getHeuristic(tempBoard);
                      possibleMoves.push({ idx: mIdx, h });
                      if (h < minH) {
                          minH = h;
                          bestMove = mIdx;
                      }
                  }
                  
                  if (bestMove !== -1) {
                    const newB = [...prev];
                    [newB[blankIdx], newB[bestMove]] = [newB[bestMove], newB[blankIdx]];
                    return newB;
                  }
                  return prev;
              });
          }, speed);
      }

      return () => {
          if (botIntervalRef.current) {
              clearInterval(botIntervalRef.current);
              botIntervalRef.current = null;
          }
      };
  }, [gameStarted, gameOver, gameMode, difficulty]); // No p1Board or p2Board, so bot doesn't reset


  // --------------------------------------------------------------------------
  // Controls
  // --------------------------------------------------------------------------
  useEffect(() => {
      const handleKeyDown = (e) => {
          if (!gameStarted || gameOver) return;

          const executeMove = (board, setBoard, key) => {
              setBoard(prev => {
                  const blankIdx = prev.indexOf(0);
                  const r = Math.floor(blankIdx / SIZE);
                  const c = blankIdx % SIZE;
                  let target = -1;

                  if (key === 'up' && r > 0) target = blankIdx - SIZE;
                  else if (key === 'down' && r < SIZE - 1) target = blankIdx + SIZE;
                  else if (key === 'left' && c > 0) target = blankIdx - 1;
                  else if (key === 'right' && c < SIZE - 1) target = blankIdx + 1;

                  if (target !== -1) {
                      const nb = [...prev];
                      [nb[blankIdx], nb[target]] = [nb[target], nb[blankIdx]];
                      return nb;
                  }
                  return prev;
              });
          };

          // P1 Keys
          if (e.key === 'w' || e.key === 'W') executeMove(p1Board, setP1Board, 'up');
          if (e.key === 's' || e.key === 'S') executeMove(p1Board, setP1Board, 'down');
          if (e.key === 'a' || e.key === 'A') executeMove(p1Board, setP1Board, 'left');
          if (e.key === 'd' || e.key === 'D') executeMove(p1Board, setP1Board, 'right');

          // P2 Keys (Only in PvP or if user wants to play P2 side?)
          if (gameMode === "PvP") {
            if (e.key === 'ArrowUp') executeMove(p2Board, setP2Board, 'up');
            if (e.key === 'ArrowDown') executeMove(p2Board, setP2Board, 'down');
            if (e.key === 'ArrowLeft') executeMove(p2Board, setP2Board, 'left');
            if (e.key === 'ArrowRight') executeMove(p2Board, setP2Board, 'right');
          }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameStarted, gameOver, gameMode]); // No p1Board or p2Board, rely fully on setBoard(prev => ...)

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="mb-4">
        <button
          onClick={() => (onBack ? onBack() : navigate("/games"))}
          className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
        >
          ← Back to Games
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-xl p-8">
        <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">15 Puzzle Race</h1>
        <p className="text-center text-gray-600 mb-8">
             Arrange the numbers 1 to 15 in order. First to finish wins!
        </p>

        {!gameStarted ? (
           <div className="text-center animate-fade-in">
             <GameOptions
               gameMode={gameMode}
               setGameMode={setGameMode}
               difficulty={difficulty}
               setDifficulty={setDifficulty}
               onStart={startNextGame}
             />
             <PlayerNameInput
                 player1Name={player1Name}
                 setPlayer1Name={setPlayer1Name}
                 player2Name={player2Name}
                 setPlayer2Name={setPlayer2Name}
                 gameMode={gameMode}
             />
             
             <div className="mt-8 text-gray-600 max-w-2xl mx-auto text-left">
               <h3 className="font-semibold mb-4 text-lg">Detailed Rules:</h3>
               <ul className="list-disc pl-5 space-y-2 bg-gray-50 p-4 rounded-lg">
                   <li><strong>Goal:</strong> Order tiles 1-15, left to right, top to bottom.</li>
                   <li><strong>Controls (P1):</strong> Click tiles or use <strong>WASD</strong>.</li>
                   <li><strong>Controls (P2):</strong> Use <strong>Arrow Keys</strong> (in Two Player Mode).</li>
                   <li><strong>Race:</strong> Both players start with the SAME shuffle.</li>
                   <li><strong>PvP Mode:</strong> RACE to solve it first! Real-time speed.</li>
               </ul>
             </div>
           </div>
        ) : (
           <div className="animate-fade-in">
               
               {/* HUD */}
               <div className="text-center mb-8">
                   {gameOver ? (
                       <div className="animate-bounce-in">
                           <Trophy className={`w-12 h-12 mx-auto mb-2 ${winner===1?'text-blue-500':'text-red-500'}`} />
                           <div className={`text-4xl font-bold ${winner===1?'text-blue-600':'text-red-600'}`}>
                               {winner === 1 ? player1Name : (gameMode === 'PvP' ? player2Name : 'Computer')} Wins!
                           </div>
                           <button 
                                onClick={startNextGame} 
                                className="mt-6 flex items-center gap-2 px-8 py-3 bg-green-500 text-white font-bold rounded-full hover:bg-green-600 transition-colors shadow-lg mx-auto"
                            >
                                <RefreshCw className="w-5 h-5" />
                                Rematch
                            </button>
                       </div>
                   ) : (
                       <div className="text-2xl font-bold text-gray-700 animate-pulse">
                           RACE IN PROGRESS!
                       </div>
                   )}
               </div>

               {/* Split Screen Boards */}
               <div className="flex flex-col md:flex-row justify-center items-start gap-8 md:gap-16">
                   
                   {/* Player 1 */}
                   <div className="relative">
                       {winner === 1 && <div className="absolute -top-12 left-1/2 -translate-x-1/2 text-4xl">👑</div>}
                       <BoardView 
                           board={p1Board} 
                           setFn={setP1Board} 
                           label={player1Name}
                           color="text-blue-600"
                           isLocalPlayer={true}
                           playerId={1}
                           gameStarted={gameStarted}
                           gameOver={gameOver}
                       />
                   </div>

                   {/* VS Divider */}
                   <div className="hidden md:flex flex-col justify-center h-[400px]">
                       <div className="h-full w-px bg-gray-200"></div>
                   </div>

                   {/* Player 2 */}
                   <div className="relative">
                       {winner === 2 && <div className="absolute -top-12 left-1/2 -translate-x-1/2 text-4xl">👑</div>}
                       <BoardView 
                           board={p2Board} 
                           setFn={setP2Board} 
                           label={gameMode === 'PvC' ? 'Computer' : player2Name} 
                           color="text-red-600"
                           isLocalPlayer={gameMode === 'PvP'}
                           playerId={2}
                           gameStarted={gameStarted}
                           gameOver={gameOver}
                       />
                   </div>

               </div>
               
               {!gameOver && (
                   <div className="flex justify-center mt-12">
                        <button onClick={() => setGameStarted(false)} className="text-gray-400 hover:text-gray-600 underline">
                            Quit Race
                        </button>
                   </div>
               )}

           </div>
        )}
      </div>
    </div>
  );
};

export default FifteenPuzzleGame;

